import { Fragment, useEffect, useMemo, useState } from 'react';

import { Checkbox } from '@/components/ui/checkbox';
import type { Customer } from '@/features/intelligence-dashboard/customer.schema';
import type { Product } from '@/features/product-scan/product-scan.schema';

import { PAGE_SIZE } from '../campaigns-config';
import { ActiveFiltersRow } from './ActiveFiltersRow';
import { ColumnHeaderMenu, type HeaderFilterOption } from './ColumnHeaderMenu';
import {
  COLUMN_FILTER_KEYS,
  EMPTY_COLUMN_FILTERS,
  type ColumnFilters,
  type LedgerFilters,
  filterCustomers,
} from './ledger-filter';
import {
  BALANCE_OPTIONS,
  HOLDER_AGE_OPTIONS,
  HOLDINGS_OPTIONS,
  LAST_OPTIONS,
  MATCH_OPTIONS,
  NAME_OPTIONS,
  SIGNALS_OPTIONS,
  TENURE_OPTIONS,
  TYPE_OPTIONS,
  type ColumnFilterOption,
} from './column-filter-options';
import { DEFAULT_SORT, type SortColumn, type SortState, getSortComparator } from './ledger-sort';
import type { SortDirection } from '../campaigns.types';
import { LedgerRow } from './LedgerRow';
import { useReferralsStore, type Referral } from '@/features/referrals/referrals-store';

interface CustomerLedgerProps {
  customers: Customer[];
  filters: LedgerFilters;
  selectedIds: Set<string>;
  /** Currently inspected (drawer-open) customer id; highlights its row. */
  activeId: string | null;
  onToggleSelect: (id: string) => void;
  onToggleSelectAll: (ids: string[], allSelected: boolean) => void;
  onOpen: (
    id: string,
  ) => void; /** Rendered inside the card, above the table (v6 .ledger-meta row — SCRUM-353). */
  header?: React.ReactNode;
  /**
   * Drawer open (v6 .ledger-area.with-inspector): the table gets a horizontal
   * scroll context + min-width so the shrunken card doesn't cramp columns.
   */
  inspecting?: boolean;
  /** SCRUM-389 tellerMode: false removes the selection column entirely. */
  selectable?: boolean;
  /**
   * SCRUM-867 column filters. OPTIONAL by design (plan RT3): a page that does not
   * pass `onColumnFiltersChange` renders no filter controls and behaves exactly as
   * before — which is what keeps the teller Customer Dashboard unchanged.
   */
  columnFilters?: ColumnFilters;
  onColumnFiltersChange?: (next: ColumnFilters) => void;
}

/**
 * SCRUM-977 — the fixed Ryan filter catalogues per column, plus the active-option
 * key so the menu can mark "Remove filter". Branch/Region options are DERIVED
 * from the loaded customers (tenant data), so they are built at render time.
 *
 * `optionKey` is the JSON of the filter value — a stable identity the menu uses
 * to match the currently-applied filter, and the token we map back to a value
 * when the user selects an option.
 */
type FilterableKey = keyof ColumnFilters;

const FIXED_OPTIONS: Partial<Record<FilterableKey, ColumnFilterOption<never>[]>> = {
  name: NAME_OPTIONS as ColumnFilterOption<never>[],
  type: TYPE_OPTIONS as ColumnFilterOption<never>[],
  tenure: TENURE_OPTIONS as ColumnFilterOption<never>[],
  holderAge: HOLDER_AGE_OPTIONS as ColumnFilterOption<never>[],
  balance: BALANCE_OPTIONS as ColumnFilterOption<never>[],
  holdings: HOLDINGS_OPTIONS as ColumnFilterOption<never>[],
  signals: SIGNALS_OPTIONS as ColumnFilterOption<never>[],
  match: MATCH_OPTIONS as ColumnFilterOption<never>[],
  last: LAST_OPTIONS as ColumnFilterOption<never>[],
};

/** Options for a column — fixed Ryan catalogue, or derived branch/region values. */
function optionsForColumn(
  key: FilterableKey,
  customers: Customer[],
): { label: string; value: ColumnFilters[FilterableKey] }[] | undefined {
  if (key === 'branch' || key === 'region') {
    const present = [...new Set(customers.map((c) => c[key]).filter(Boolean))].sort();
    return present.map((v) => ({ label: v, value: v }));
  }
  const fixed = FIXED_OPTIONS[key];
  return fixed?.map((o) => ({ label: o.label, value: o.value }));
}

/** Builds the ColumnHeaderMenu filter props for a column, or undefined if sort-only. */
function filterMenuFor(
  key: SortColumn,
  columnFilters: ColumnFilters,
  onChange: (next: ColumnFilters) => void,
  customers: Customer[],
): {
  filterOptions: HeaderFilterOption[];
  activeOptionKey: string | null;
  onFilter: (optionKey: string) => void;
  onClearFilter: () => void;
} | null {
  // The TENURE column's SortColumn key is `age` (legacy Customer.ageMo), but its
  // filter key is `tenure`. Bridge that one alias so the column resolves its
  // Ryan bucket catalogue; every other column's sort key equals its filter key.
  const fkCandidate = key === 'age' ? 'tenure' : key;
  // `recent` never appears as a column; only real ColumnFilters keys are filterable.
  if (!(COLUMN_FILTER_KEYS as readonly string[]).includes(fkCandidate)) return null;
  const fk = fkCandidate as FilterableKey;
  const opts = optionsForColumn(fk, customers);
  if (!opts || opts.length === 0) return null;

  const withKeys = opts.map((o) => ({
    label: o.label,
    optionKey: JSON.stringify(o.value),
    value: o.value,
  }));
  const current = columnFilters[fk];
  const activeOptionKey = current == null ? null : JSON.stringify(current);

  return {
    filterOptions: withKeys.map(({ label, optionKey }) => ({ label, optionKey })),
    activeOptionKey,
    onFilter: (optionKey) => {
      const hit = withKeys.find((o) => o.optionKey === optionKey);
      if (hit) onChange({ ...columnFilters, [fk]: hit.value });
    },
    onClearFilter: () => {
      onChange({ ...columnFilters, [fk]: null });
    },
  };
}

// The MATCH column shows only when a product filter is active.

interface ColumnDef {
  key: SortColumn;
  label: string;
  align: 'left' | 'right';
}

// SCRUM-977 — Ryan's exact header order (7b.html L11324-11337): Customer, Type,
// Holder Age, Tenure, Branch, Balance, Holdings, Signals, [Match], Last. Region
// is our SCRUM-956 addition, kept adjacent to Branch. GEO_COLUMNS (Branch+Region)
// are spliced in after Tenure when showGeo; MATCH before LAST when showMatch.
const COLUMNS: readonly ColumnDef[] = [
  { key: 'name', label: 'CUSTOMER', align: 'left' },
  { key: 'type', label: 'TYPE', align: 'left' },
  { key: 'holderAge', label: 'HOLDER AGE', align: 'right' },
  { key: 'age', label: 'TENURE', align: 'right' },
  { key: 'balance', label: 'BALANCE', align: 'right' },
  { key: 'holdings', label: 'HOLDINGS', align: 'left' },
  { key: 'signals', label: 'SIGNALS', align: 'left' },
  { key: 'last', label: 'LAST', align: 'right' },
];

/**
 * SCRUM-956 — the geography columns, inserted before LAST when geo is shown.
 *
 * Gated for the same reason SCRUM-867 gated the filter controls (its RT3): this one
 * component also renders the TELLER Customer Dashboard, which passes no filter props,
 * and a Teller is branch-scoped — so BRANCH/REGION there would be two columns of a
 * constant value. Both the header and the body read `showGeo` from THIS single
 * boolean, because `LedgerRow` lays its cells out by hand: two independent conditions
 * would be free to disagree and shift every following cell.
 */
const GEO_COLUMNS: readonly ColumnDef[] = [
  { key: 'branch', label: 'BRANCH', align: 'left' },
  { key: 'region', label: 'REGION', align: 'left' },
];

/**
 * The customer intelligence ledger table (SCRUM-206). Each header is a
 * ColumnHeaderMenu (SCRUM-977) merging sort + single-select filter into one
 * dropdown (Ryan's `.col-menu`), replacing the prior whole-<th> 3-click sort
 * cycle (SCRUM-280) and standalone funnel popover (SCRUM-867). Also: free-text
 * search, 25-per-page pagination, and select-all over the full filtered set
 * (not just the visible page).
 */
export function CustomerLedger({
  customers,
  filters,
  selectedIds,
  activeId,
  onToggleSelect,
  onToggleSelectAll,
  selectable = true,
  columnFilters = EMPTY_COLUMN_FILTERS,
  onColumnFiltersChange,
  onOpen,
  header,
  inspecting = false,
}: CustomerLedgerProps) {
  const [sort, setSort] = useState<SortState>(DEFAULT_SORT);
  const [page, setPage] = useState(0);

  // SCRUM-868: any change to the effective filter set — applying/removing a
  // Product Insight, editing a column filter, or typing in search — must return
  // the grid to page 1. Otherwise a page index from a larger result set persists
  // and `safePage` clamps it to the LAST page of the new, smaller set (the clamp
  // hid this rather than fixing it: Rob applied an insight and landed on page 4/4
  // instead of 1). `filters` is a stable CampaignsPage useMemo keyed on exactly
  // search + appliedInsight + columnFilters, so this fires on a real filter change
  // and NOT on unrelated re-renders. Sort resets its own page in setSortFromMenu
  // because sort is local state, not part of `filters`.
  useEffect(() => {
    setPage(0);
  }, [filters]);
  // SCRUM-335: customers with a non-denied referral get the has-referral row
  // treatment and FLOAT TO THE TOP regardless of the active sort (CJ's
  // requirement: an active referral is the first thing an operator sees).
  const referrals = useReferralsStore((st) => st.referrals);
  const referralByCustomer = useMemo(() => {
    const map = new Map<string, Referral>();
    for (const r of referrals) {
      if (r.status !== 'denied' && r.customerId && !map.has(r.customerId)) {
        map.set(r.customerId, r);
      }
    }
    return map;
  }, [referrals]);

  const matchProducts: Product[] = filters.productMatches;
  const showMatch = matchProducts.length > 0;
  // Total columns = (checkbox when selectable) + COLUMNS + (MATCH when shown).
  // Geography rides with the filter affordance — see GEO_COLUMNS.
  const showGeo = onColumnFiltersChange !== undefined;
  // Ryan places Branch (+ our Region) BETWEEN Tenure and Balance. COLUMNS is
  // …HolderAge, Tenure(age), Balance… so splice the geo columns in after `age`.
  const columns = useMemo(() => {
    if (!showGeo) return COLUMNS;
    const at = COLUMNS.findIndex((c) => c.key === 'age') + 1;
    return [...COLUMNS.slice(0, at), ...GEO_COLUMNS, ...COLUMNS.slice(at)];
  }, [showGeo]);
  const colCount = columns.length + (selectable ? 1 : 0) + (showMatch ? 1 : 0);

  // Filter → sort. Recomputed only when inputs change.
  const filtered = useMemo(() => {
    const result = filterCustomers(customers, filters);
    result.sort(getSortComparator(sort.column, sort.direction, matchProducts));
    // Stable partition: referral rows first (SCRUM-335 float-to-top), each
    // group keeping the user's chosen sort order internally.
    const withRef = result.filter((c) => referralByCustomer.has(c.id));
    const withoutRef = result.filter((c) => !referralByCustomer.has(c.id));
    return [...withRef, ...withoutRef];
  }, [customers, filters, sort, referralByCustomer, matchProducts]);

  const total = filtered.length;
  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const safePage = Math.min(page, pageCount - 1);
  const start = safePage * PAGE_SIZE;
  const pageRows = filtered.slice(start, start + PAGE_SIZE);

  // Select-all operates over the entire filtered set, not just the page.
  const filteredIds = useMemo(() => filtered.map((c) => c.id), [filtered]);
  const allFilteredSelected =
    filteredIds.length > 0 && filteredIds.every((id) => selectedIds.has(id));

  // SCRUM-977 — sort now comes from explicit menu items (Ryan's setSortFromMenu),
  // not a whole-<th> 3-click cycle. Choosing a sort resets to page 1, matching
  // Ryan's campaignsResetPage() and preserving the SCRUM-868 page-reset intent
  // (sort is local state, NOT part of `filters`, so the filter useEffect above
  // does not cover it — this handler must reset the page itself).
  function setSortFromMenu(column: SortColumn, direction: SortDirection) {
    setSort({ column, direction });
    setPage(0);
  }

  return (
    <div className="border-border-1 bg-bg-4 overflow-hidden rounded-lg border">
      {header}
      {/* SCRUM-867 (RT5): visible evidence of an active filter, so a reduced row
          count never looks like missing data. Renders nothing when no column
          filter is set, and only when the page opted in. */}
      {onColumnFiltersChange ? (
        <ActiveFiltersRow filters={columnFilters} onChange={onColumnFiltersChange} />
      ) : null}
      {/* SCRUM-956: the horizontal scroll context is ALWAYS on. It used to apply only
          while the drawer was open, which was fine at 8 columns but BRANCH + REGION take
          the table to 10 (11 with MATCH) — without a min-width the columns would simply
          crush at a narrow viewport instead of scrolling, and the frontend rules are explicit that
          wide tables scroll inside their own container rather than deform. min-width
          scales with the extra columns; the page remains the vertical scroller
          (SCRUM-353 D4). */}
      <div className="overflow-x-auto">
        <table
          className={`w-full border-collapse text-sm ${inspecting ? 'min-w-[1300px]' : 'min-w-[1180px]'}`}
        >
          <thead>
            <tr className="border-border-0 border-b">
              {selectable && (
                <th className="bg-bg-3 w-9 px-3 py-2">
                  <Checkbox
                    checked={allFilteredSelected}
                    onCheckedChange={() => {
                      onToggleSelectAll(filteredIds, allFilteredSelected);
                    }}
                    aria-label="Select all filtered customers"
                  />
                </th>
              )}
              {columns.map((col) => {
                // MATCH pseudo-column (Ryan: 'Match' single product / 'Best Match'
                // multiple) appears before LAST only when a product filter is active.
                // Ryan both sorts AND filters it (MATCH_OPTIONS), so its menu carries a
                // filter section too — filterMenuFor('match', …) supplies it.
                const matchHdr =
                  col.key === 'last' && showMatch ? (
                    <th
                      key="match"
                      className="text-text-3 bg-bg-3 px-3 py-2 text-left font-mono text-[0.625rem] font-semibold tracking-[0.08em] uppercase"
                    >
                      <ColumnHeaderMenu
                        column="match"
                        label={matchProducts.length === 1 ? 'MATCH' : 'BEST MATCH'}
                        sort={sort}
                        onSort={setSortFromMenu}
                        {...(onColumnFiltersChange
                          ? (filterMenuFor(
                              'match',
                              columnFilters,
                              onColumnFiltersChange,
                              customers,
                            ) ?? {})
                          : {})}
                      />
                    </th>
                  ) : null;

                const menu = onColumnFiltersChange
                  ? filterMenuFor(col.key, columnFilters, onColumnFiltersChange, customers)
                  : null;

                return (
                  <Fragment key={col.key}>
                    {matchHdr}
                    <th
                      className={`text-text-3 bg-bg-3 px-3 py-2 font-mono text-[0.625rem] font-semibold tracking-[0.08em] uppercase select-none ${
                        col.align === 'right' ? 'text-right' : 'text-left'
                      } ${sort.column === col.key ? 'text-brand-primary' : ''}`}
                    >
                      <ColumnHeaderMenu
                        column={col.key}
                        label={col.label}
                        align={col.align}
                        sort={sort}
                        onSort={setSortFromMenu}
                        {...(menu ?? {})}
                      />
                    </th>
                  </Fragment>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {pageRows.map((c) => (
              <LedgerRow
                showGeo={showGeo}
                key={c.id}
                customer={c}
                selected={selectedIds.has(c.id)}
                selectable={selectable}
                active={c.id === activeId}
                onToggleSelect={onToggleSelect}
                onOpen={onOpen}
                referral={referralByCustomer.get(c.id) ?? null}
                {...(showMatch ? { matchProducts } : {})}
              />
            ))}
            {total === 0 && (
              <tr>
                <td colSpan={colCount} className="text-text-3 px-3 py-12 text-center">
                  No customers match the current filters.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {total > 0 && (
        <div className="border-border-1 text-text-2 flex items-center justify-between border-t px-3 py-2 text-xs">
          <span>
            Showing {(start + 1).toLocaleString('en-US')}–
            {Math.min(start + PAGE_SIZE, total).toLocaleString('en-US')} of{' '}
            {total.toLocaleString('en-US')}
          </span>
          <div className="flex items-center gap-1">
            <PageButton
              label="« First"
              disabled={safePage === 0}
              onClick={() => {
                setPage(0);
              }}
            />
            <PageButton
              label="‹ Prev"
              disabled={safePage === 0}
              onClick={() => {
                setPage(safePage - 1);
              }}
            />
            <span className="px-2">
              Page {safePage + 1} of {pageCount}
            </span>
            <PageButton
              label="Next ›"
              disabled={safePage >= pageCount - 1}
              onClick={() => {
                setPage(safePage + 1);
              }}
            />
            <PageButton
              label="Last »"
              disabled={safePage >= pageCount - 1}
              onClick={() => {
                setPage(pageCount - 1);
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
}

function PageButton({
  label,
  disabled,
  onClick,
}: {
  label: string;
  disabled: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className="border-border-1 text-text-1 hover:bg-bg-3 rounded border px-2 py-1 disabled:cursor-not-allowed disabled:opacity-40"
    >
      {label}
    </button>
  );
}
